import {t} from 'i18next';
import {Step} from '../../common/arr-stepper/ArrStepper';
import {ReProductionTypeCodeEnum} from '../../../api/types/reproduction.type';
import {ISelectItem} from '../../../shared/types';
import {ExtendedTreeItemProps} from '../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view/models';

// Route constant
export const ROUTE_REPRODUCTION = '/Production/Home/ReProduction';
export const ROUTE_REPRODUCTION_EDIT = '/Production/Home/ReProduction/Edit';
export const QUERY_PARAM_REPRODUCTION_ID = 'productionId';

// Step keys
export const STEP_KEY_BASIC_INFO = 'basic-info';
export const STEP_KEY_SELECT_SCOPE = 'select-scope';
export const STEP_KEY_SELECT_PRODUCTIONS = 'select-productions';
export const STEP_KEY_PRODUCTION_COMPONENTS = 'production-components';
export const STEP_KEY_NUMBERING = 'numbering';
export const STEP_KEY_OUTPUT_SETTINGS = 'output-settings';
export const STEP_KEY_GENERATE = 'generate';
export const STEP_KEY_QC_CONFIRMATION = 'qc-confirmation';

export enum ReProductionType {
  IMAGE_NATIVE_TO_PLACEHOLDER = 'IMAGE_NATIVE_TO_PLACEHOLDER',
  ADD_REMOVE_REDACTIONS = 'ADD_REMOVE_REDACTIONS',
  PLACEHOLDER_TO_IMAGE_NATIVE = 'PLACEHOLDER_TO_IMAGE_NATIVE',
  ADD_REMOVE_BRANDING = 'ADD_REMOVE_BRANDING',
}

const reProductionTypeCodeMap: Record<
  ReProductionType,
  ReProductionTypeCodeEnum
> = {
  [ReProductionType.IMAGE_NATIVE_TO_PLACEHOLDER]:
    ReProductionTypeCodeEnum.NATIVETOPLACEHOLDER,
  [ReProductionType.ADD_REMOVE_REDACTIONS]:
    ReProductionTypeCodeEnum.ADDREMREDACTION,
  [ReProductionType.PLACEHOLDER_TO_IMAGE_NATIVE]:
    ReProductionTypeCodeEnum.PLACEHOLDERTONATIVE,
  [ReProductionType.ADD_REMOVE_BRANDING]:
    ReProductionTypeCodeEnum.ADDREMBRANDING,
};

export const toReProductionTypeCodeEnum = (
  type: ReProductionType | null
): ReProductionTypeCodeEnum | null => {
  if (!type) return null;
  const entries = Object.entries(reProductionTypeCodeMap) as [
    ReProductionType,
    ReProductionTypeCodeEnum,
  ][];
  const entry = entries.find(([key]) => key === type);
  return entry ? entry[1] : null;
};

export const fromReProductionTypeCodeEnum = (
  typeCode: ReProductionTypeCodeEnum | null | undefined
): ReProductionType | null => {
  if (typeCode === null || typeCode === undefined) return null;

  const matchedType = (
    Object.keys(reProductionTypeCodeMap) as ReProductionType[]
  ).find(
    (type) =>
      Object.prototype.hasOwnProperty.call(reProductionTypeCodeMap, type) &&
      reProductionTypeCodeMap[type as keyof typeof reProductionTypeCodeMap] ===
        typeCode
  );

  return matchedType ?? null;
};

export interface ReProductionTypeOption {
  value: ReProductionType;
  label: string;
  description: string;
}

export const getReProductionTypeOptions = (): ReProductionTypeOption[] => [
  {
    value: ReProductionType.IMAGE_NATIVE_TO_PLACEHOLDER,
    label: t(
      'reProduction.basicInfo.form.types.options.imageNativeToPlaceholder.label'
    ),
    description: t(
      'reProduction.basicInfo.form.types.options.imageNativeToPlaceholder.description'
    ),
  },
  {
    value: ReProductionType.ADD_REMOVE_REDACTIONS,
    label: t(
      'reProduction.basicInfo.form.types.options.addRemoveRedactions.label'
    ),
    description: t(
      'reProduction.basicInfo.form.types.options.addRemoveRedactions.description'
    ),
  },
  {
    value: ReProductionType.PLACEHOLDER_TO_IMAGE_NATIVE,
    label: t(
      'reProduction.basicInfo.form.types.options.placeholderToImageNative.label'
    ),
    description: t(
      'reProduction.basicInfo.form.types.options.placeholderToImageNative.description'
    ),
  },
  {
    value: ReProductionType.ADD_REMOVE_BRANDING,
    label: t(
      'reProduction.basicInfo.form.types.options.addRemoveBranding.label'
    ),
    description: t(
      'reProduction.basicInfo.form.types.options.addRemoveBranding.description'
    ),
  },
];

/**
 * Returns stepper items for the re-production wizard.
 * Follows the same pattern as stepperItems() in CreateNewProductionUtils.
 */
export const reProductionStepperItems = (): Step[] => [
  {
    key: STEP_KEY_BASIC_INFO,
    label: t('reProduction.steps.basicInfo'),
    state: 'current',
    tooltipTitle: t('reProduction.steps.basicInfo'),
  },
  {
    key: STEP_KEY_SELECT_SCOPE,
    label: t('reProduction.steps.selectScope'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.selectScope'),
  },
  {
    key: STEP_KEY_SELECT_PRODUCTIONS,
    label: t('reProduction.steps.selectProductions'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.selectProductions'),
  },
  {
    key: STEP_KEY_PRODUCTION_COMPONENTS,
    label: t('reProduction.steps.productionComponents'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.productionComponents'),
    tooltip: t('reProduction.steps.productionComponentsTooltip'),
  },
  {
    key: STEP_KEY_NUMBERING,
    label: t('reProduction.steps.numbering'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.numbering'),
    tooltip: t(
      'productionsAndExports.createNewProduction.stepper.numberingAndSorting.tooltip'
    ),
  },
  {
    key: STEP_KEY_OUTPUT_SETTINGS,
    label: t('reProduction.steps.outputSettings'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.outputSettings'),
    tooltip: t('reProduction.steps.outputSettingsTooltip'),
  },
  {
    key: STEP_KEY_GENERATE,
    label: t('reProduction.steps.generate'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.generate'),
    tooltip: t(
      'productionsAndExports.createNewProduction.stepper.generate.tooltip'
    ),
  },
  {
    key: STEP_KEY_QC_CONFIRMATION,
    label: t('reProduction.steps.qcConfirmation'),
    state: 'future',
    tooltipTitle: t('reProduction.steps.qcConfirmation'),
    tooltip: t('reProduction.steps.qcConfirmationTooltip'),
  },
];

/**
 * Returns step config (title, tooltip) for StepWrapper.
 * This is used in StepWrapper to display the step title and tooltip based on the current step key.
 */
// Step-specific configuration interface
export interface StepConfig {
  showHeaderNav?: boolean;
  showFooterNav?: boolean;
  showSaveButton?: boolean;
  showNextButton?: boolean;
}

export const STEP_CONFIG: Partial<Record<string, StepConfig>> = {
  [STEP_KEY_GENERATE]: {
    showFooterNav: false,
    showSaveButton: false,
  },
  [STEP_KEY_QC_CONFIRMATION]: {
    showFooterNav: false,
    showSaveButton: false,
    showNextButton: false,
  },
};

export const getReProductionStepConfig = (
  stepKey: string
): {
  stepTitle: string;
  stepTooltipTitle: string;
  stepTooltip: string;
  showHeaderNav?: boolean;
  showFooterNav?: boolean;
  showSaveButton?: boolean;
  showNextButton?: boolean;
} => {
  const stepMap: Record<
    string,
    {stepTitle: string; stepTooltipTitle: string; stepTooltip: string}
  > = {
    [STEP_KEY_BASIC_INFO]: {
      stepTitle: t('reProduction.steps.basicInfo'),
      stepTooltipTitle: t('reProduction.steps.basicInfo'),
      stepTooltip: t('reProduction.steps.basicInfoTooltip'),
    },
    [STEP_KEY_SELECT_SCOPE]: {
      stepTitle: t('reProduction.steps.selectScope'),
      stepTooltipTitle: t('reProduction.steps.selectScope'),
      stepTooltip: t('reProduction.steps.selectScopeTooltip'),
    },
    [STEP_KEY_SELECT_PRODUCTIONS]: {
      stepTitle: t('reProduction.steps.selectProductions'),
      stepTooltipTitle: t('reProduction.steps.selectProductions'),
      stepTooltip: t('reProduction.steps.selectProductionsTooltip'),
    },
    [STEP_KEY_PRODUCTION_COMPONENTS]: {
      stepTitle: t('reProduction.steps.productionComponents'),
      stepTooltipTitle: t('reProduction.steps.productionComponents'),
      stepTooltip: t('reProduction.steps.productionComponentsTooltip'),
    },
    [STEP_KEY_NUMBERING]: {
      stepTitle: t('reProduction.steps.numbering'),
      stepTooltipTitle: t('reProduction.steps.numbering'),
      stepTooltip: t(
        'productionsAndExports.createNewProduction.stepper.numberingAndSorting.tooltip'
      ),
    },
    [STEP_KEY_OUTPUT_SETTINGS]: {
      stepTitle: t('reProduction.steps.outputSettings'),
      stepTooltipTitle: t('reProduction.steps.outputSettings'),
      stepTooltip: t('reProduction.steps.outputSettingsTooltip'),
    },
    [STEP_KEY_GENERATE]: {
      stepTitle: t('reProduction.steps.generate'),
      stepTooltipTitle: t('reProduction.steps.generate'),
      stepTooltip: t(
        'productionsAndExports.createNewProduction.stepper.generate.tooltip'
      ),
    },
    [STEP_KEY_QC_CONFIRMATION]: {
      stepTitle: t('reProduction.steps.qcConfirmation'),
      stepTooltipTitle: t('reProduction.steps.qcConfirmation'),
      stepTooltip: t('reProduction.steps.qcConfirmationTooltip'),
    },
  };

  if (Object.prototype.hasOwnProperty.call(stepMap, stepKey)) {
    const baseConfig = stepMap[stepKey as keyof typeof stepMap];
    // eslint-disable-next-line security/detect-object-injection
    const stepConfig = STEP_CONFIG[stepKey];

    return {
      ...baseConfig,
      showHeaderNav: stepConfig?.showHeaderNav !== false,
      showFooterNav: stepConfig?.showFooterNav !== false,
      showSaveButton: stepConfig?.showSaveButton !== false,
      showNextButton: stepConfig?.showNextButton !== false,
    };
  }

  return {
    stepTitle: '',
    stepTooltipTitle: '',
    stepTooltip: '',
    showHeaderNav: true,
    showFooterNav: true,
    showSaveButton: true,
    showNextButton: true,
  };
};

/** Resolves numeric re-production id from edit-mode API (field name varies by serializer). */
export const getReProductionIdFromEditResponse = (data: {
  reproductionId?: number;
  reProductionId?: number;
  productionId?: number;
}): number => {
  const raw =
    data.reproductionId ?? data.reProductionId ?? data.productionId ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** Resolves reProductionTypeCode from edit-mode API response (field name varies by serializer). */
export const getReProductionTypeFromEditResponse = (data: {
  reProductionTypeCode?: string | ReProductionTypeCodeEnum;
  ReProductionTypeCode?: string | ReProductionTypeCodeEnum;
  reproductionTypeCode?: string | ReProductionTypeCodeEnum;
  reProductionType?: string | ReProductionTypeCodeEnum;
}): ReProductionType | null => {
  const raw =
    data.reProductionTypeCode ??
    data.ReProductionTypeCode ?? // ← PascalCase variant (matches reproduction.type.ts interface)
    data.reproductionTypeCode ??
    data.reProductionType ??
    null;

  if (raw === null || raw === undefined) return null;

  console.log('[getReProductionTypeFromEditResponse] raw:', raw);

  // If already a numeric enum value
  if (typeof raw === 'number') {
    return fromReProductionTypeCodeEnum(raw as ReProductionTypeCodeEnum);
  }

  // If string key e.g. "PLACEHOLDERTONATIVE" → look up in enum
  const numericCode =
    ReProductionTypeCodeEnum[raw as keyof typeof ReProductionTypeCodeEnum];

  console.log(
    '[getReProductionTypeFromEditResponse] numericCode:',
    numericCode
  );

  return fromReProductionTypeCodeEnum(numericCode ?? null);
};

/** Post-generation QC complete — wizard remains editable until commit. */
export const isReProductionQcCompleteEditableState = (
  data: {productionStateCode?: string} | null | undefined
): boolean => {
  if (!data) return false;

  return (
    String(data.productionStateCode ?? '').toUpperCase() ===
    ProductionState.QCCOMPLETE
  );
};

/** Post-uncommit states — wizard steps remain fully editable. */
export const isReProductionPostUncommitEditableState = (
  data:
    | {
        productionCurrentStatus?: number | string;
        productionStatusCode?: string | number;
        productionStateCode?: string;
      }
    | null
    | undefined
): boolean => {
  if (!data) return false;

  const currentStatus = Number(data.productionCurrentStatus);
  const statusCode = String(data.productionStatusCode ?? '').toUpperCase();
  const stateCode = String(data.productionStateCode ?? '').toUpperCase();

  if (currentStatus === 1) return false;

  return (
    stateCode === ProductionState.UNCOMMITED ||
    statusCode === ProductionStatus.DRAFT
  );
};

/** Wizard read-only status from edit/view API (empty = fully editable). */
export function resolveReProductionWizardStatusCode(
  data:
    | {
        productionCurrentStatus?: number | string;
        productionStatusCode?: string | number;
        productionStateCode?: string;
      }
    | null
    | undefined
): string {
  if (!data) return '';

  if (isReProductionPostUncommitEditableState(data)) {
    return '';
  }

  if (isReProductionQcCompleteEditableState(data)) {
    return '';
  }

  const currentStatus = Number(data.productionCurrentStatus);
  if (currentStatus === 2) return ProductionStatus.COMPLETED;
  if (currentStatus === 1) return ProductionStatus.INPROGRESS;

  const statusCode = String(data.productionStatusCode ?? '').toUpperCase();
  if (statusCode === ProductionStatus.COMPLETED) {
    return ProductionStatus.COMPLETED;
  }
  if (statusCode === ProductionStatus.INPROGRESS) {
    return ProductionStatus.INPROGRESS;
  }

  const stateCode = String(data.productionStateCode ?? '').toUpperCase();
  if (stateCode === 'COMMITTED' || stateCode === 'GENERATECOMPLETE') {
    return ProductionStatus.COMPLETED;
  }
  if (stateCode === 'INPROGRESS' || stateCode.endsWith('INPROGRESS')) {
    return ProductionStatus.INPROGRESS;
  }

  return '';
}

export const getReProductionStepKeyFromNumber = (
  stepNumber: number
): string => {
  const stepMap: {[key: number]: string} = {
    1: STEP_KEY_BASIC_INFO,
    2: STEP_KEY_SELECT_SCOPE,
    3: STEP_KEY_SELECT_PRODUCTIONS,
    4: STEP_KEY_PRODUCTION_COMPONENTS,
    5: STEP_KEY_NUMBERING,
    6: STEP_KEY_OUTPUT_SETTINGS,
    7: STEP_KEY_GENERATE,
    8: STEP_KEY_QC_CONFIRMATION,
  };

  const key = String(stepNumber);
  const value = Object.entries(stepMap).find(([k]) => k === key);
  return value ? value[1] : STEP_KEY_BASIC_INFO;
};

/** Prior production reference */
export interface PriorProductionRef {
  productionId: number;
  productionName: string;
}

export interface ReProductionFormData {
  // Basic Info
  productionId: number;
  name: string;
  description: string;
  reProductionType: ReProductionType | null;
  // Prior production selection
  priorProduction: PriorProductionRef | null;
  // Component settings loaded from prior production
  componentSettings?: Record<string, unknown>;

  // Select Scope
  savedSearchId?: number;
  savedSearchName?: string;
  folderIds?: string[];
  tagIds?: string[];
  searchTerms?: string;
  includeFamily?: boolean;

  // Select Productions
  selectedProductionIds: number[];

  // Production Components
  productionComponents?: Record<string, unknown>;

  // Numbering
  numbering?: {
    minNumberLength?: number;
  };

  // Output Settings
  outputSettings?: Record<string, unknown>;

  // QCConfirmation
  selectedProductionNames: string[];
}

export const DEFAULT_FORM_DATA: ReProductionFormData = {
  productionId: 0,
  name: '',
  description: '',
  reProductionType: null,
  priorProduction: null,
  componentSettings: undefined,
  savedSearchId: undefined,
  savedSearchName: undefined,
  folderIds: [],
  tagIds: [],
  searchTerms: '',
  includeFamily: false,
  selectedProductionIds: [],
  productionComponents: {},
  numbering: {},
  outputSettings: {},
  selectedProductionNames: [],
};

export enum ToastType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
}

// Production status enum
export enum ProductionStatus {
  FAILED = 'FAILED',
  INPROGRESS = 'INPROGRESS',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
}

// Form mode enum
export enum FormMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view',
}

// Module type enum
export enum ModuleType {
  PRODUCTION = 'PRODUCTION',
  EXPORT = 'EXPORT',
  REPRODUCTION = 'REPRODUCTION',
}

// Form action enum (used across all form components)
export enum FormAction {
  SAVE = 'save',
  NEXT = 'next',
}
/**
 * Detailed editability rules per Re-Production type.
 * Derived from the requirements specification.
 *
 * Section-level: which accordion sections are visible / editable / locked
 * Sub-section-level: within TIFF/PDF and Native, which sub-sections are editable
 */

export interface SectionEditability {
  /** Section is visible in the UI */
  visible: boolean;
  /** Checkbox is always checked and locked (cannot be unchecked) */
  checkboxLocked: boolean;
  /** The section content mode: 'view' = read-only, 'edit' = editable */
  contentMode: 'view' | 'edit';
}

export interface TiffSubSectionEditability {
  pageSettings: boolean; // Options, Type, Size, Rotate
  doNotProduceTiff: boolean;
  blankPageRemoval: boolean;
  preserveColor: boolean;
  branding: boolean;
  placeholders: boolean;
  redactions: boolean;
}

export interface NativeSubSectionEditability {
  fileSettings: boolean; // all native file settings
  generateLst: boolean; // Generate LST setting
  redactions: boolean; // Burn redactions subsection
}

export interface DatSubSectionEditability {
  datFields: boolean; // merged DAT field list — read-only
  scrubbing: boolean; // scrubbing checkboxes — editable
}

export interface ReProductionEditabilityRules {
  dat: SectionEditability & {subSections: DatSubSectionEditability};
  native: SectionEditability & {subSections: NativeSubSectionEditability};
  tiffPdf: SectionEditability & {subSections: TiffSubSectionEditability};
  text: SectionEditability;
  mp3: SectionEditability; // ← ADD
  translation: SectionEditability; // ← ADD
}

/**
 * Returns the full editability rules for a given ReProductionType.
 */
export function getReProductionEditabilityRules(reProductionType?: string) {
  switch (reProductionType) {
    case ReProductionType.IMAGE_NATIVE_TO_PLACEHOLDER:
      return {
        dat: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {datFields: false, scrubbing: true},
        },
        native: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            fileSettings: true,
            generateLst: false,
            redactions: false,
          },
        },
        tiffPdf: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            pageSettings: false,
            doNotProduceTiff: false,
            blankPageRemoval: false,
            preserveColor: false,
            branding: false,
            placeholders: true,
            redactions: false,
          },
        },
        text: {visible: true, checkboxLocked: true, contentMode: 'view'},
        mp3: {visible: false, checkboxLocked: true, contentMode: 'view'},
        translation: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
      };

    case ReProductionType.PLACEHOLDER_TO_IMAGE_NATIVE:
      return {
        dat: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {datFields: false, scrubbing: true},
        },
        native: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            fileSettings: true,
            generateLst: false,
            redactions: false,
          },
        },
        tiffPdf: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            pageSettings: false,
            doNotProduceTiff: false,
            blankPageRemoval: false,
            preserveColor: false,
            branding: false,
            placeholders: true,
            redactions: false,
          },
        },
        text: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
        },
        mp3: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
        translation: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
      };

    case ReProductionType.ADD_REMOVE_REDACTIONS:
      return {
        dat: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {
            datFields: false,
            scrubbing: true,
          },
        },
        native: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            fileSettings: false,
            generateLst: false,
            redactions: true,
          },
        },
        tiffPdf: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            pageSettings: false,
            branding: false,
            placeholders: false,
            redactions: true,
          },
        },
        text: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
        },
        mp3: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
        translation: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
      };

    case ReProductionType.ADD_REMOVE_BRANDING:
      return {
        dat: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {datFields: false, scrubbing: false},
        },
        native: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {
            fileSettings: false,
            generateLst: false,
            redactions: false,
          },
        },
        tiffPdf: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'edit',
          subSections: {
            pageSettings: false,
            doNotProduceTiff: false,
            blankPageRemoval: false,
            preserveColor: false,
            branding: true,
            placeholders: false,
            redactions: false,
          },
        },
        text: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
        },
        mp3: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
        translation: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
      };

    default:
      return {
        dat: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {datFields: false, scrubbing: false},
        },
        native: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {
            fileSettings: false,
            generateLst: false,
            redactions: false,
          },
        },
        tiffPdf: {
          visible: true,
          checkboxLocked: true,
          contentMode: 'view',
          subSections: {
            pageSettings: false,
            doNotProduceTiff: false,
            blankPageRemoval: false,
            preserveColor: false,
            branding: false,
            placeholders: false,
            redactions: false,
          },
        },
        text: {visible: true, checkboxLocked: true, contentMode: 'view'},
        mp3: {visible: false, checkboxLocked: true, contentMode: 'view'},
        translation: {
          visible: false,
          checkboxLocked: true,
          contentMode: 'view',
        },
      };
  }
}

/**
 * Maps each ReProductionType to the production component section IDs
 * that should be editable. These IDs match the section.id values
 * in ProductionComponents.tsx: 'DAT', 'Native', 'TIFF / PDF', 'Text'
 */
export const RE_PRODUCTION_EDITABLE_SECTIONS: Record<
  ReProductionType,
  string[]
> = {
  [ReProductionType.IMAGE_NATIVE_TO_PLACEHOLDER]: ['TIFF / PDF'],
  [ReProductionType.ADD_REMOVE_REDACTIONS]: ['TIFF / PDF', 'Native'],
  [ReProductionType.PLACEHOLDER_TO_IMAGE_NATIVE]: ['TIFF / PDF', 'Native'],
  [ReProductionType.ADD_REMOVE_BRANDING]: ['TIFF / PDF'],
};

/**
 * Maps each ReProductionType to the TIFF/PDF sub-sections that should
 * be editable. Sub-section IDs must match what TiffPdfComponent uses internally.
 */
export const RE_PRODUCTION_TIFF_EDITABLE_SUBSECTIONS: Record<
  ReProductionType,
  string[]
> = {
  [ReProductionType.IMAGE_NATIVE_TO_PLACEHOLDER]: ['placeholder'],
  [ReProductionType.ADD_REMOVE_REDACTIONS]: ['redaction'],
  [ReProductionType.PLACEHOLDER_TO_IMAGE_NATIVE]: [
    'pageSettings',
    'placeholder',
  ],
  [ReProductionType.ADD_REMOVE_BRANDING]: ['branding'],
};

export enum ProductionState {
  INPROGRESS = 'INPROGRESS',
  PAGEDATACOMPLETED = 'PAGEDATACOMPLETED',
  PAGECOUNTINPROGRESS = 'PAGECOUNTINPROGRESS',
  PAGECOUNTCOMPLETED = 'PAGECOUNTCOMPLETED',
  DATAGENINPROGRESS = 'DATAGENINPROGRESS',
  AFTERDATAGENFAILED = 'AFTERDATAGENFAILED',
  COPYINPROGRESS = 'COPYINPROGRESS',
  ARCHIVEINPROGRESS = 'ARCHIVEINPROGRESS',
  GENERATECOMPLETE = 'GENERATECOMPLETE',
  QCINPROGRESS = 'QCINPROGRESS',
  QCCOMPLETE = 'QCCOMPLETE',
  FAILED = 'FAILED',
  UNCOMMITFAILED = 'UNCOMMITFAILED',
  COMMITFAILED = 'COMMITFAILED',
  UNCOMMITINPROGRESS = 'UNCOMMITINPROGRESS',
  COMMITINPROGRESS = 'COMMITINPROGRESS',
  UNCOMMITED = 'UNCOMMITED',
  COMPLETED = 'COMPLETED',
  CANCELINPROGRESS = 'CANCELINPROGRESS',
  CANCELCOMPLETED = 'CANCELCOMPLETED',
  CANCELFAILED = 'CANCELFAILED',
}
const RE_PRODUCTION_COMMIT_IN_PROGRESS_STATES = [
  'COMMITLSTGENINP',
  'COMMITDATAGENINP',
  'COMMITCOPYINP',
  'COMMITARCHIVINP',
  'COMMITLSTGENCMP',
  'COMMITDATAGENCMP',
  'COMMITCOPYCMP',
  'COMMITARCHIVCMP',
] as const;

const RE_PRODUCTION_UNCOMMIT_IN_PROGRESS_STATES = [
  'UNCOMMITLSTGENINP',
  'UNCOMMITDATAGENINP',
  'UNCOMMITCOPYINP',
  'UNCOMMITARCHIVINP',
  'UNCOMMITLSTGENCMP',
  'UNCOMMITDATAGENCMP',
  'UNCOMMITCOPYCMP',
  'UNCOMMITARCHIVCMP',
] as const;

const RE_PRODUCTION_COMMIT_FAILED_STATES = [
  'COMMITLSTGENFAIL',
  'COMMITDATAGENFAIL',
  'COMMITCOPYFAIL',
  'COMMITARCHIVFAIL',
] as const;

const RE_PRODUCTION_UNCOMMIT_FAILED_STATES = [
  'UNCOMMITLSTGENFAIL',
  'UNCOMMITDATAGENFAIL',
  'UNCOMMITCOPYFAIL',
  'UNCOMMITARCHIVFAIL',
] as const;

const RE_PRODUCTION_COMMIT_WORKFLOW_IN_PROGRESS_STATES = [
  ...RE_PRODUCTION_COMMIT_IN_PROGRESS_STATES,
  ...RE_PRODUCTION_UNCOMMIT_IN_PROGRESS_STATES,
] as const;

const RE_PRODUCTION_COMMIT_WORKFLOW_FAILED_STATES = [
  ...RE_PRODUCTION_COMMIT_FAILED_STATES,
  ...RE_PRODUCTION_UNCOMMIT_FAILED_STATES,
] as const;

const RE_PRODUCTION_EXTENDED_NEXT_STATE_CODES = [
  ...RE_PRODUCTION_COMMIT_WORKFLOW_IN_PROGRESS_STATES,
  ...RE_PRODUCTION_COMMIT_WORKFLOW_FAILED_STATES,
] as const;

const RE_PRODUCTION_BASE_NEXT_STATES: readonly ProductionState[] = [
  ProductionState.UNCOMMITED,
  ProductionState.QCCOMPLETE,
  ProductionState.GENERATECOMPLETE,
  ProductionState.UNCOMMITINPROGRESS,
  ProductionState.COMMITINPROGRESS,
  ProductionState.COMPLETED,
];

const RE_PRODUCTION_NEXT_STATE_SET = new Set<string>([
  ...RE_PRODUCTION_BASE_NEXT_STATES,
  ...RE_PRODUCTION_EXTENDED_NEXT_STATE_CODES,
]);

const RE_PRODUCTION_POLLING_ALLOWED_STATE_SET = new Set<string>([
  ProductionState.QCCOMPLETE,
  ProductionState.GENERATECOMPLETE,
  ProductionState.COMPLETED,
  ...RE_PRODUCTION_EXTENDED_NEXT_STATE_CODES,
]);

const RE_PRODUCTION_COMMIT_IN_PROGRESS_SET = new Set<string>(
  RE_PRODUCTION_COMMIT_IN_PROGRESS_STATES
);
const RE_PRODUCTION_UNCOMMIT_IN_PROGRESS_SET = new Set<string>(
  RE_PRODUCTION_UNCOMMIT_IN_PROGRESS_STATES
);
const RE_PRODUCTION_COMMIT_FAILED_SET = new Set<string>(
  RE_PRODUCTION_COMMIT_FAILED_STATES
);
const RE_PRODUCTION_UNCOMMIT_FAILED_SET = new Set<string>(
  RE_PRODUCTION_UNCOMMIT_FAILED_STATES
);
const RE_PRODUCTION_COMMIT_WORKFLOW_IN_PROGRESS_SET = new Set<string>(
  RE_PRODUCTION_COMMIT_WORKFLOW_IN_PROGRESS_STATES
);
const RE_PRODUCTION_COMMIT_WORKFLOW_FAILED_SET = new Set<string>(
  RE_PRODUCTION_COMMIT_WORKFLOW_FAILED_STATES
);

const normalizeReProductionStateCode = (state?: string | null) =>
  String(state ?? '').toUpperCase();

/** True when generate view shows post-generation QC complete (state or status text). */
export const isReProductionPostQcCheckComplete = (
  stateCode?: string | null,
  displayStatus?: string | null
): boolean => {
  const state = normalizeReProductionStateCode(stateCode);
  if (
    state === ProductionState.QCCOMPLETE ||
    state === ProductionState.GENERATECOMPLETE
  ) {
    return true;
  }

  const display = String(displayStatus ?? '').toLowerCase();
  if (!display) return false;

  return display.includes('post-generation qc') && display.includes('complete');
};

export const canProceedToNextStep = (state?: string) =>
  !!state &&
  RE_PRODUCTION_NEXT_STATE_SET.has(normalizeReProductionStateCode(state));

export const isAllowedNextStepStateWhilePolling = (state?: string) =>
  !!state &&
  RE_PRODUCTION_POLLING_ALLOWED_STATE_SET.has(
    normalizeReProductionStateCode(state)
  );

export const isReProductionCommitWorkflowInProgressState = (state?: string) => {
  const normalizedState = normalizeReProductionStateCode(state);
  return (
    normalizedState === ProductionState.COMMITINPROGRESS ||
    normalizedState === ProductionState.UNCOMMITINPROGRESS ||
    RE_PRODUCTION_COMMIT_WORKFLOW_IN_PROGRESS_SET.has(normalizedState)
  );
};

export const isReProductionCommitWorkflowFailedState = (state?: string) => {
  const normalizedState = normalizeReProductionStateCode(state);
  return (
    normalizedState === ProductionState.COMMITFAILED ||
    normalizedState === ProductionState.UNCOMMITFAILED ||
    RE_PRODUCTION_COMMIT_WORKFLOW_FAILED_SET.has(normalizedState)
  );
};

export const isReProductionCommitFailedState = (state?: string) => {
  const normalizedState = normalizeReProductionStateCode(state);
  return (
    normalizedState === ProductionState.COMMITFAILED ||
    RE_PRODUCTION_COMMIT_FAILED_SET.has(normalizedState)
  );
};

export const isReProductionUncommitFailedState = (state?: string) => {
  const normalizedState = normalizeReProductionStateCode(state);
  return (
    normalizedState === ProductionState.UNCOMMITFAILED ||
    RE_PRODUCTION_UNCOMMIT_FAILED_SET.has(normalizedState)
  );
};

export const getReProductionStatusFromStateCode = (
  state?: string | null
): string | null => {
  const normalizedState = normalizeReProductionStateCode(state);

  if (
    normalizedState === ProductionState.COMMITINPROGRESS ||
    RE_PRODUCTION_COMMIT_IN_PROGRESS_SET.has(normalizedState)
  ) {
    return t('reProduction.status.commitInProgress');
  }
  if (
    normalizedState === ProductionState.UNCOMMITINPROGRESS ||
    RE_PRODUCTION_UNCOMMIT_IN_PROGRESS_SET.has(normalizedState)
  ) {
    return t('reProduction.status.uncommitInProgress');
  }
  if (
    normalizedState === ProductionState.UNCOMMITFAILED ||
    RE_PRODUCTION_UNCOMMIT_FAILED_SET.has(normalizedState)
  ) {
    return t('reProduction.status.uncommitFailed');
  }
  if (
    normalizedState === ProductionState.COMMITFAILED ||
    RE_PRODUCTION_COMMIT_FAILED_SET.has(normalizedState)
  ) {
    return t('reProduction.status.commitFailed');
  }

  return null;
};

// Branding position mapping for TIFF/PDF components
export const BRANDING_POSITIONS = {
  'left-header': 'leftHeaderBranding',
  'right-header': 'rightHeaderBranding',
  'center-header': 'centerHeaderBranding',
  'left-center-header': 'leftCenterHeaderBranding',
  'right-center-header': 'rightCenterHeaderBranding',
  header: 'headerBranding',
  'left-footer': 'leftFooterBranding',
  'right-footer': 'rightFooterBranding',
  'center-footer': 'centerFooterBranding',
  'left-center-footer': 'leftCenterFooterBranding',
  'right-center-footer': 'rightCenterFooterBranding',
  footer: 'footerBranding',
} as const;

// Shared constants used across multiple components

// Default branding constants (used in multiple components)
export const DEFAULT_FONT_FAMILY = 'Arial';
export const DEFAULT_FONT_STYLE = 'Regular';
export const DEFAULT_FONT_SIZE = '10';
// Component type enum
export enum ComponentType {
  TIFF = 'TIFF',
  PDF = 'PDF',
}

// Tree ID constants for slip sheet groups in ArrTreeItemSelector
export const SlipSheetTreeId = {
  FOLDER_TREE: 'folder-tree',
  TAG_TREE: 'tag-tree',
  REDACTION_TAG_TREE: 'redaction-tag-tree',
  COMMENT_TREE: 'comment-tree',
  ASSIGNMENT_TREE: 'assignment-tree',
  METADATA_TREE: 'metadata-tree',
  CALCULATED_TREE: 'calculated-tree',
} as const;

export enum DocumentSelectionMethod {
  FILE_TYPE = 'file-type',
  TAGS = 'tags',
  SEARCHES = 'searches',
  FOLDERS = 'folders',
}

// Preserve color option enum
export enum PreserveColorOption {
  SPECIFY_DOCS = 'specify-docs',
  ALL_DOCS = 'all-docs',
}
// Static dropdown options for TIFF/PDF Component
export const getFontOptions = (): ISelectItem[] => [
  {value: 'Arial', label: 'Arial'},
  {value: 'Comic Sans MS', label: 'Comic Sans'},
  {value: 'Courier New', label: 'Courier New'},
  {value: 'Georgia', label: 'Georgia'},
  {value: 'Times', label: 'Times New Roman'},
  {value: 'Verdana', label: 'Verdana'},
];

export const getFontStyleOptions = (): ISelectItem[] => [
  {value: 'Regular', label: 'Regular'},
  {value: 'Italic', label: 'Italic'},
  {value: 'Bold', label: 'Bold'},
];

export const getFontSizeOptions = (): ISelectItem[] => [
  {value: '6', label: '6'},
  {value: '8', label: '8'},
  {value: '9', label: '9'},
  {value: '10', label: '10'},
  {value: '11', label: '11'},
  {value: '12', label: '12'},
  {value: '14', label: '14'},
  {value: '16', label: '16'},
];

export const getInsertFieldTypes = (): ISelectItem[] => [
  {
    value: 'metadata',
    label: t(
      'productionsAndExports.createNewProduction.common.toolbar.metadataField'
    ),
  },
  {
    value: 'production',
    label: t(
      'productionsAndExports.createNewProduction.common.toolbar.productionField'
    ),
  },
];

export const getPageOptions = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.generateTiff'
    ),
    value: 0,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.generatePdf'
    ),
    value: 1,
  },
];

export const getPageTypes = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.multiPage'
    ),
    value: 1,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.singlePage'
    ),
    value: 0,
  },
];

export const getPageSizes = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.letter'
    ),
    value: 0,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.a4'
    ),
    value: 1,
  },
];

export const getRotationOptions = (): ISelectItem[] => [
  {
    value: '0',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.noRotation'
    ),
  },
  {
    value: '-90',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.rotateCounterClockwise90'
    ),
  },
  {
    value: '90',
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.options.rotateClockwise90'
    ),
  },
];

export const getPreserveColorOptions = (): ISelectItem[] => [
  {
    value: PreserveColorOption.SPECIFY_DOCS,
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.colorJpgOptions.specifyDocs'
    ),
  },
  {
    value: PreserveColorOption.ALL_DOCS,
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.colorJpgOptions.allDocs'
    ),
  },
];

export const getDocumentSelectionMethods = (): ISelectItem[] => [
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.fileType'
    ),
    value: DocumentSelectionMethod.FILE_TYPE,
  },
  {
    label: t(
      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.tags'
    ),
    value: DocumentSelectionMethod.TAGS,
  },
];

export const getRedactionTypeOptions = (): ISelectItem[] => [
  {
    value: 1,
    label: 'All Redactions in annotation layer: Default Annotation Layer',
  },
  {value: 0, label: 'Select a Redaction Tag'},
];

// Utility function to convert flat array to tree structure
export const transformTagTreeData = (
  flatArray: any[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const itemMap = new Map();
  const rootItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  // First pass: create all items
  flatArray.forEach((item) => {
    itemMap.set(item.id, {
      id: item.id,
      label: item.text,
      children: [],
      isGroup: item.isGroup,
      type: item.type,
      documentCount: item.documentCount,
      isSelected: item.isSelected,
      // Add other properties as needed
      ...item,
    });
  });

  // Second pass: build hierarchy
  flatArray.forEach((item) => {
    const treeItem = itemMap.get(item.id);

    if (
      item.parent === '#' ||
      item.parent === null ||
      item.parent === undefined
    ) {
      // Root level items (parent is "#" or null/undefined)
      rootItems.push(treeItem);
    } else {
      // Child item - find parent and add to its children
      const parent = itemMap.get(item.parent);
      if (parent) {
        parent.children.push(treeItem);
      } else {
        // Parent not found, treat as root item
        rootItems.push(treeItem);
      }
    }
  });

  return rootItems;
};
